import React, { useEffect, useState } from 'react'
import { Plus, Trash01, Eraser } from '@untitled-ui/icons-react'
import { debounce } from 'lodash'
import { format, formatISO, parseISO } from 'date-fns'
import {
  MyAutocomplete,
  MyBgPatternDecorativeCircle,
  MyCalendar,
  MyPopper,
  MyTextField,
} from '@interstellar-component'

/**
 * Client-side fork of interstellar-component's MyFilterModal.
 *
 * Same UI/behaviour, but emits on EVERY change while dropping incomplete
 * conditions, instead of only emitting once every row is fully valid. The
 * upstream component never calls onChange when a value is cleared, which leaves
 * the parent holding a stale filter (the result never returns to its unfiltered
 * state). Here, clearing a value drops that condition and re-emits, so the data
 * resets as expected.
 *
 * Scope is intentionally trimmed to string / number / date fields — the only
 * types the report's purely client-side filters use (no endpoint-backed
 * select/multi lookups). Keep it in sync with the shared component if the
 * upstream UI changes.
 */
const isComplete = (e) =>
  Boolean(e?.field) &&
  Boolean(e?.condition) &&
  !(e?.value === undefined || e?.value === null || (e?.value === '' && typeof e?.value !== 'boolean'))

function MyClientFilterModal({ id, target, currentFilters, onChange }) {
  const [options, setOptions] = useState([])
  const [filter, setFilter] = useState([])

  const addFilter = () => {
    const data = { field: null, condition: null, value: null, operator: null }
    if (filter.length === 1) data.operator = { label: 'and', value: 'and' }
    if (filter.length >= 2) data.operator = filter[1].operator
    setFilter([...filter, data])
  }

  const clearFilter = () => setFilter([])

  const removeFilter = (index) => {
    if (filter && filter.length) {
      const newFilter = filter.slice()
      newFilter.splice(index, 1)
      setFilter(newFilter)
    }
  }

  // The combinator (and/or) is global — applies to every condition.
  const handleChangeOperator = (e, value) => {
    setFilter(filter.map((row) => ({ ...row, operator: value })))
  }

  const handleChangeField = (e, value, index) => {
    const temp = filter.slice()
    temp[index].filter = value
    temp[index].field = value?.name ?? ''
    temp[index].condition = null
    temp[index].value = null
    setFilter(temp)
  }

  const handleChangeCondition = (e, value, index) => {
    const temp = filter.slice()
    temp[index].condition = value
    setFilter(temp)
  }

  const handleChangeValue = (value, index) => {
    const temp = filter.slice()
    temp[index].value = value
    setFilter(temp)
  }

  // Emit on every change, sending only the fully-specified conditions so that
  // clearing a value (or partially configuring a row) resets / updates results.
  useEffect(() => {
    if (onChange) onChange({ ...filter.filter(isComplete) })
  }, [filter])

  useEffect(() => {
    setOptions(currentFilters)
  }, [currentFilters])

  return (
    <MyPopper
      id={id}
      haveValue={filter.filter((e) => e.value)?.length}
      target={target}
      placement="bottom-start"
    >
      {() => (
        <div className="flex w-[881px] flex-col">
          <div className="z-10 p-6 pb-5">
            <MyBgPatternDecorativeCircle originClass="items-center justify-end">
              <p className="text-sm-regular text-gray-light/600">In this view, show records.</p>
            </MyBgPatternDecorativeCircle>
          </div>
          <div className="z-20 flex flex-col gap-y-4">
            <div className="flex flex-col gap-4 px-4">
              {(filter ?? []).map((e, i) => (
                <div key={i} className="flex w-full items-center gap-4">
                  <div className="w-24">
                    {i === 0 ? (
                      <div className="px-[14px] py-2">
                        <p className="tex-md-regular text-gray-light/900">Where</p>
                      </div>
                    ) : i === 1 ? (
                      <MyAutocomplete
                        disableClearable
                        options={[
                          { label: 'and', value: 'and' },
                          { label: 'or', value: 'or' },
                        ]}
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        value={e?.operator}
                        placeholder="-"
                        onChange={(ev, value) => handleChangeOperator(ev, value, i)}
                        focusColor="#0A2349"
                        focusShadow="#DCE3F1"
                      />
                    ) : (
                      <div className="px-[14px] py-2">
                        <p className="tex-md-regular text-gray-light/900">{e?.operator?.label}</p>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <MyAutocomplete
                      disableClearable
                      value={e?.filter}
                      placeholder="Field"
                      options={options}
                      isOptionEqualToValue={(option, value) => option?.name === value?.name}
                      onChange={(ev, value) => handleChangeField(ev, value, i)}
                      onInputFocus={() => setOptions(currentFilters)}
                      onInputChange={(ev) => {
                        const text = ev?.target?.value
                        setOptions((value) => {
                          if (text)
                            return value.filter((opt) =>
                              opt?.label.toLowerCase().includes(text?.toLowerCase())
                            )
                          return currentFilters
                        })
                      }}
                      focusColor="#0A2349"
                      focusShadow="#DCE3F1"
                    />
                  </div>
                  <div className="flex-1">
                    {e?.filter?.options && (
                      <MyAutocomplete
                        disableClearable
                        options={e?.filter?.options ?? []}
                        value={e.condition}
                        placeholder="Condition"
                        isOptionEqualToValue={(option, value) => option.value === value.value}
                        onChange={(ev, value) => handleChangeCondition(ev, value, i)}
                        focusColor="#0A2349"
                        focusShadow="#DCE3F1"
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    {e?.condition && e?.filter && (
                      <FilterInput element={e} onChange={(value) => handleChangeValue(value, i)} />
                    )}
                  </div>
                  <button onClick={() => removeFilter(i)} className="p-3">
                    <Trash01 className="size-5 text-gray-light/600" stroke="currentColor" />
                  </button>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between p-6 px-4 pt-8">
              <button onClick={addFilter} className="flex items-center gap-2">
                <Plus className="size-5 text-gray-light/600" stroke="currentColor" />
                <p className="text-md-semibold text-gray-light/600">Add condition</p>
              </button>
              <button onClick={clearFilter} className="flex items-center gap-2">
                <Eraser className="size-5 text-gray-light/600" stroke="currentColor" />
                <p className="text-md-semibold text-gray-light/600">Clear</p>
              </button>
            </div>
          </div>
        </div>
      )}
    </MyPopper>
  )
}

function FilterInput({ element, onChange }) {
  const type = element?.filter?.type

  if (type === 'string' || type === 'number') {
    return (
      <MyTextField
        placeholder="Enter your value"
        value={element.value}
        type={type === 'number' ? 'number' : undefined}
        onChangeForm={(e) => {
          if (onChange) debounce((ev) => onChange(ev?.target?.value), 500)(e)
        }}
        focusColor="#0A2349"
        focusShadow="#DCE3F1"
      />
    )
  }

  if (type === 'date') {
    return (
      <MyCalendar
        value={element.value && parseISO(element.value)}
        target={(open, handleClick) => (
          <button
            onClick={handleClick}
            type="button"
            className="flex w-full items-center gap-1 rounded-lg border border-gray-light/300 px-3.5 py-2.5 text-gray-light/700 shadow-shadows/shadow-xs"
          >
            {element?.value ? (
              <p className="text-md-regular px-0.5 text-gray-light/900">
                {format(parseISO(element?.value), 'MMM d, yyyy')}
              </p>
            ) : (
              <p className="text-md-regular px-0.5 text-gray-light/500">Enter your date</p>
            )}
          </button>
        )}
        onChange={(date) => {
          if (date && onChange) onChange(formatISO(date, { representation: 'complete' }))
        }}
      />
    )
  }

  return null
}

export default MyClientFilterModal
